import React from 'react';

import {
  createIdempotencyKey,
  getKycQueue,
  getKycSubmission,
  getProtectedFile,
  requestKycRevision,
  reviewKyc,
} from '../../api/rental';
import Layout from './Layout';
import VerificationPanel from './VerificationPanel';
import {
  ActionButton,
  Actions,
  Badge,
  Card,
  CardTitle,
  ErrorText,
  Field,
  Grid,
  List,
  ListButton,
  Select,
  State,
  SuccessText,
  Textarea,
} from './styles';
import {riskLabel, riskLevelMeta} from './verification-view';
import styled from 'styled-components';

const documentLabels = {
  passport_main: 'Разворот паспорта',
  passport_registration: 'Страница регистрации',
  driver_license_front: 'ВУ — лицевая сторона',
  driver_license_back: 'ВУ — обратная сторона',
  selfie_with_passport: 'Селфи с открытым паспортом',
};

const statusLabels = {
  draft: 'Черновик',
  pending: 'Ожидает решения',
  revision: 'На пересъёмке',
  approved: 'Одобрено',
  rejected: 'Отклонено',
};

const formatDate = value => value ? new Date(value).toLocaleString('ru-RU') : '—';

function ProtectedImage({authorization, document, retake, onRetakeChange}) {
  const [url, setUrl] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    let active = true;
    let objectUrl = '';
    setError('');
    getProtectedFile(authorization, document.file_id)
      .then(blob => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(requestError => active && setError(requestError.message));

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [authorization, document.file_id]);

  const download = async () => {
    try {
      const blob = await getProtectedFile(authorization, document.file_id, true);
      const downloadUrl = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = downloadUrl;
      link.download = `${document.type}.${document.mime_type === 'image/png' ? 'png' : 'jpg'}`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    } catch (requestError) {
      setError(requestError.message);
    }
  };

  return (
    <DocumentCard>
      <DocumentTitle>{documentLabels[document.type] || document.type}</DocumentTitle>
      <ImageFrame>
        {!url && !error && <State>Загрузка защищённого изображения…</State>}
        {error && <State error>{error}</State>}
        {url && <DocumentImage src={url} alt={documentLabels[document.type] || 'Документ KYC'} />}
      </ImageFrame>
      <DocumentMeta>{Math.ceil(Number(document.size || 0) / 1024)} КБ · {document.mime_type}</DocumentMeta>
      {document.status === 'rejected' && (
        <RetakeNote>На пересъёмке: {document.reject_reason}</RetakeNote>
      )}
      <ActionButton type="button" onClick={download}>Скачать с аудитом</ActionButton>

      {onRetakeChange && (
        <RetakeBlock>
          <label>
            <input
              type="checkbox"
              checked={Boolean(retake)}
              onChange={event => onRetakeChange(
                document.type,
                event.target.checked ? {reason: (retake && retake.reason) || ''} : null,
              )}
            />
            {' '}Переснять этот документ
          </label>
          {retake && (
            <Textarea
              short
              rows={2}
              placeholder="Что не так со снимком — текст увидит клиент"
              value={retake.reason}
              onChange={event => onRetakeChange(document.type, {reason: event.target.value})}
            />
          )}
        </RetakeBlock>
      )}
    </DocumentCard>
  );
}

function DocumentsContent({authorization}) {
  const [status, setStatus] = React.useState('pending');
  const [items, setItems] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState('');
  const [submission, setSubmission] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [detailsLoading, setDetailsLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [retakes, setRetakes] = React.useState({});
  const commandKey = React.useRef('');

  const changeRetake = (type, value) => setRetakes(current => {
    const next = Object.assign({}, current);
    if (value) { next[type] = value; } else { delete next[type]; }
    return next;
  });

  const loadQueue = React.useCallback(() => {
    setLoading(true);
    setError('');
    return getKycQueue(authorization, status)
      .then(data => {
        setItems(Array.isArray(data) ? data : []);
        if (selectedId && !(data || []).some(item => item.id === selectedId)) {
          setSelectedId('');
          setSubmission(null);
        }
      })
      .catch(requestError => setError(requestError.message))
      .finally(() => setLoading(false));
  }, [authorization, selectedId, status]);

  React.useEffect(() => { loadQueue(); }, [loadQueue]);

  React.useEffect(() => {
    if (!selectedId) return;
    setDetailsLoading(true);
    setError('');
    getKycSubmission(authorization, selectedId)
      .then(setSubmission)
      .catch(requestError => setError(requestError.message))
      .finally(() => setDetailsLoading(false));
  }, [authorization, selectedId]);

  const decide = async decision => {
    if (decision === 'reject' && !reason.trim()) {
      setError('Для отказа укажите причину — она будет показана пользователю.');
      return;
    }
    setSaving(true);
    setError('');
    setMessage('');
    if (!commandKey.current) commandKey.current = createIdempotencyKey(`kyc-${decision}`);
    try {
      await reviewKyc(authorization, selectedId, decision, reason.trim(), commandKey.current);
      setMessage(decision === 'approve' ? 'Комплект одобрен.' : 'Комплект отклонён.');
      commandKey.current = '';
      setReason('');
      setSubmission(null);
      setSelectedId('');
      await loadQueue();
    } catch (requestError) {
      setError(`${requestError.message}. Повторите действие — будет использован тот же ключ команды.`);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Пересъёмка отличается от отказа: комплект остаётся у клиента, он
   * переснимает только отмеченные документы и возвращается в очередь.
   */
  const sendToRevision = async () => {
    const documents = Object.keys(retakes).map(type => ({
      type,
      reason: (retakes[type].reason || '').trim(),
    }));
    if (!documents.length) {
      setError('Отметьте хотя бы один документ для пересъёмки.');
      return;
    }
    if (documents.some(item => !item.reason)) {
      setError('У каждого документа должна быть причина — её увидит клиент.');
      return;
    }

    setSaving(true);
    setError('');
    setMessage('');
    try {
      await requestKycRevision(authorization, selectedId, documents);
      setMessage('Документы отправлены на пересъёмку. Комплект вернётся в очередь после замены.');
      setRetakes({});
      setSubmission(null);
      setSelectedId('');
      await loadQueue();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Grid>
      <Card>
        <CardTitle>Очередь KYC</CardTitle>
        <Field>
          Статус
          <Select value={status} onChange={event => {
            setStatus(event.target.value);
            setSelectedId('');
            setSubmission(null);
          }}>
            <option value="pending">Ожидают решения</option>
            <option value="revision">На пересъёмке</option>
            <option value="approved">Одобренные</option>
            <option value="rejected">Отклонённые</option>
            <option value="draft">Черновики</option>
          </Select>
        </Field>
        <div style={{height: 16}} />
        {loading && <State>Загрузка очереди…</State>}
        {!loading && error && !submission && <State error>{error}</State>}
        {!loading && !error && items.length === 0 && <State>В этой очереди нет комплектов.</State>}
        {!loading && items.length > 0 && (
          <List>
            {items.map(item => (
              <ListButton
                key={item.id}
                active={item.id === selectedId}
                onClick={() => { setSelectedId(item.id); setMessage(''); setReason(''); commandKey.current = ''; }}
              >
                <strong>{item.phone || item.user_id}</strong>
                <QueueMeta>Версия {item.version} · {formatDate(item.submitted_at || item.created_at)}</QueueMeta>
                <Badge>{statusLabels[item.status] || item.status}</Badge>
                {item.risk_level && (
                  <Badge
                    danger={['critical', 'high'].includes(item.risk_level)}
                    success={item.risk_level === 'low'}
                    style={{marginLeft: 6}}
                    title={riskLevelMeta(item.risk_level).advice}
                  >
                    Риск: {riskLabel({level: item.risk_level, score: item.risk_score})}
                  </Badge>
                )}
              </ListButton>
            ))}
          </List>
        )}
      </Card>

      <Card>
        <CardTitle>Проверка комплекта</CardTitle>
        {!selectedId && <State>Выберите пользователя в очереди слева.</State>}
        {selectedId && detailsLoading && <State>Загрузка карточки…</State>}
        {selectedId && !detailsLoading && error && !submission && <State error>{error}</State>}
        {submission && (
          <>
            <Summary>
              <div><b>Пользователь:</b> {submission.phone || submission.user_id}</div>
              <div><b>Версия:</b> {submission.version}</div>
              <div><b>Отправлено:</b> {formatDate(submission.submitted_at)}</div>
              <Badge success={submission.status === 'approved'} danger={submission.status === 'rejected'}>
                {statusLabels[submission.status] || submission.status}
              </Badge>
            </Summary>
            <DocumentsGrid>
              {(submission.documents || []).map(document => (
                <ProtectedImage
                  key={document.file_id}
                  authorization={authorization}
                  document={document}
                  retake={retakes[document.type]}
                  onRetakeChange={submission.status === 'pending' ? changeRetake : null}
                />
              ))}
            </DocumentsGrid>
            <VerificationPanel
              authorization={authorization}
              userId={submission.user_id}
              submissionId={submission.id}
            />
            {submission.status === 'pending' && (
              <ReviewBlock>
                <Hint>
                  Проверки две и смешивать их не нужно. Плохой снимок исправим — отметьте
                  документ и напишите причину, клиент переснимет только его. Отказ закрывает
                  комплект целиком и применяется к самому клиенту, а не к качеству фото.
                </Hint>
                <Field>
                  Причина отказа
                  <Textarea
                    short
                    value={reason}
                    onChange={event => { setReason(event.target.value); commandKey.current = ''; }}
                    placeholder="Обязательна только при отказе"
                  />
                </Field>
                {error && <ErrorText role="alert">{error}</ErrorText>}
                {message && <SuccessText aria-live="polite">{message}</SuccessText>}
                <Actions>
                  <ActionButton primary disabled={saving} onClick={() => decide('approve')}>
                    {saving ? 'Сохраняем…' : 'Одобрить комплект'}
                  </ActionButton>
                  <ActionButton
                    disabled={saving || Object.keys(retakes).length === 0}
                    onClick={sendToRevision}
                  >
                    На пересъёмку ({Object.keys(retakes).length})
                  </ActionButton>
                  <ActionButton danger disabled={saving} onClick={() => decide('reject')}>
                    Отклонить
                  </ActionButton>
                </Actions>
              </ReviewBlock>
            )}
          </>
        )}
      </Card>
    </Grid>
  );
}

export default function Documents() {
  return (
    <Layout
      title="Документы проката"
      description="Ручная проверка паспорта, водительского удостоверения и селфи. Каждый просмотр файла записывается в аудит."
    >
      <DocumentsContent />
    </Layout>
  );
}

const QueueMeta = styled.div`
  margin: 5px 0 8px;
  color: #666b73;
  font-size: 12px;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 20px;
  margin-bottom: 20px;
  color: #2f3033;
  line-height: 1.5;
`;

const DocumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  @media screen and (max-width: 800px) { grid-template-columns: 1fr; }
`;

const DocumentCard = styled.article`
  min-width: 0;
  padding: 14px;
  border: 1px solid #e6eaed;
  border-radius: 8px;
`;

const DocumentTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 14px;
`;

const ImageFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 210px;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f7f9;
`;

const DocumentImage = styled.img`
  display: block;
  width: 100%;
  max-height: 360px;
  object-fit: contain;
`;

const DocumentMeta = styled.div`
  margin: 9px 0 0;
  color: #818286;
  font-size: 12px;
`;

const ReviewBlock = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e6eaed;
`;

const Hint = styled.p`
  margin: 0 0 16px;
  color: #818286;
  font-size: 12px;
  line-height: 1.5;
`;

const RetakeNote = styled.div`
  margin: 9px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  color: #df2d14;
  background: #fff5f3;
  font-size: 12px;
  line-height: 1.4;
`;

const RetakeBlock = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e6eaed;
  color: #2f3033;
  font-size: 13px;
  textarea { margin-top: 8px; }
`;
